import React, { useEffect, useRef, useState } from "react";
import { Keyboard, Pencil, Image as ImageIcon, X, RotateCcw } from "lucide-react";

// Handwriting-ish styles available without bundling extra fonts.
const FONT_STYLES = [
  { label: "Style 1", css: '"Brush Script MT", "Segoe Script", cursive' },
  { label: "Style 2", css: '"Snell Roundhand", "Apple Chancery", cursive' },
  { label: "Style 3", css: '"Lucida Handwriting", "Segoe Script", cursive' },
  { label: "Style 4", css: '"Comic Sans MS", "Bradley Hand", cursive' },
  { label: "Style 5", css: '"Bradley Hand", "Segoe Script", cursive' },
  { label: "Style 6", css: 'cursive' },
];

const TABS = [
  { key: "type", label: "Type", Icon: Keyboard },
  { key: "draw", label: "Draw", Icon: Pencil },
  { key: "image", label: "Image", Icon: ImageIcon },
];

/** Render typed text to a transparent PNG; returns { dataUrl, aspect }. */
const typedToPng = (text, fontCss) => {
  const W = 600;
  const H = 200;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = "#111827";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `64px ${fontCss}`;
  ctx.fillText(text, W / 2, H / 2);
  return { dataUrl: canvas.toDataURL("image/png"), aspect: H / W };
};

const SignatureModal = ({ open, onClose, onSave }) => {
  const [tab, setTab] = useState("type");
  const [name, setName] = useState("");
  const [fontIdx, setFontIdx] = useState(0);
  const [imageData, setImageData] = useState(null);

  const drawCanvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);

  // Reset state whenever the modal opens.
  useEffect(() => {
    if (open) {
      setTab("type");
      setName("");
      setFontIdx(0);
      setImageData(null);
      hasDrawnRef.current = false;
    }
  }, [open]);

  // Set up the draw canvas (size + listeners) when the Draw tab is shown.
  useEffect(() => {
    if (!open || tab !== "draw") return;
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";

    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return { x: p.clientX - r.left, y: p.clientY - r.top };
    };
    const start = (e) => {
      drawingRef.current = true;
      const { x, y } = pos(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
      e.preventDefault();
    };
    const move = (e) => {
      if (!drawingRef.current) return;
      const { x, y } = pos(e);
      ctx.lineTo(x, y);
      ctx.stroke();
      hasDrawnRef.current = true;
      e.preventDefault();
    };
    const end = () => {
      drawingRef.current = false;
    };
    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", end);
    return () => {
      canvas.removeEventListener("mousedown", start);
      canvas.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", end);
    };
  }, [open, tab]);

  if (!open) return null;

  const clearCanvas = () => {
    const canvas = drawCanvasRef.current;
    if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    hasDrawnRef.current = false;
  };

  const handleSave = () => {
    if (tab === "type") {
      if (!name.trim()) return;
      const { dataUrl, aspect } = typedToPng(name.trim(), FONT_STYLES[fontIdx].css);
      onSave(dataUrl, aspect);
    } else if (tab === "draw") {
      const canvas = drawCanvasRef.current;
      if (!canvas || !hasDrawnRef.current) return;
      onSave(canvas.toDataURL("image/png"), canvas.height / canvas.width);
    } else if (tab === "image") {
      if (!imageData) return;
      onSave(imageData.dataUrl, imageData.aspect);
    }
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const img = new Image();
      img.onload = () =>
        setImageData({ dataUrl, aspect: img.height / img.width || 0.4 });
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const saveDisabled =
    (tab === "type" && !name.trim()) ||
    (tab === "image" && !imageData);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-[620px] bg-white rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200">
          <h2 className="text-[16px] font-semibold text-[#18101C]">Create signature</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-[#52525B]" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-6 px-5 pt-3 border-b border-gray-100">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 pb-2.5 text-[14px] font-medium border-b-2 -mb-px transition-colors ${
                tab === key
                  ? "text-[#C95EBE] border-[#C95EBE]"
                  : "text-[#52525B] border-transparent hover:text-[#18101C]"
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === "type" && (
            <>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Type your name"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-[15px] focus:outline-none focus:border-[#C95EBE]"
                autoFocus
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
                {FONT_STYLES.map((f, i) => (
                  <button
                    key={f.label}
                    onClick={() => setFontIdx(i)}
                    className={`h-[64px] rounded-md border flex items-center justify-center overflow-hidden px-2 ${
                      fontIdx === i
                        ? "border-[#C95EBE] bg-[#FDF2FB]"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    style={{ fontFamily: f.css, fontSize: 26, color: "#111827" }}
                    title={f.label}
                  >
                    <span className="truncate">{name.trim() || "Your Name"}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {tab === "draw" && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[13px] text-[#52525B]">
                  Sign with your mouse or trackpad.
                </span>
                <button
                  onClick={clearCanvas}
                  className="flex items-center gap-1 text-[13px] text-[#C95EBE] hover:underline"
                >
                  <RotateCcw size={13} /> Clear
                </button>
              </div>
              <canvas
                ref={drawCanvasRef}
                width={560}
                height={220}
                className="w-full border border-gray-200 rounded-md bg-white touch-none"
                style={{ cursor: "crosshair" }}
              />
            </div>
          )}

          {tab === "image" && (
            <div className="text-center">
              {imageData ? (
                <img
                  src={imageData.dataUrl}
                  alt="signature"
                  className="mx-auto max-h-[180px] object-contain border border-gray-200 rounded-md p-2"
                />
              ) : (
                <label className="flex flex-col items-center justify-center gap-2 h-[180px] border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-[#C95EBE] text-[#52525B]">
                  <ImageIcon size={26} />
                  <span className="text-[13px]">Click to upload an image of your signature</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onPickImage} />
                </label>
              )}
              {imageData && (
                <label className="inline-block mt-3 text-[13px] text-[#C95EBE] hover:underline cursor-pointer">
                  Choose a different image
                  <input type="file" accept="image/*" className="hidden" onChange={onPickImage} />
                </label>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-center px-5 py-4 border-t border-gray-100">
          <button
            onClick={handleSave}
            disabled={saveDisabled}
            className="px-8 py-2 rounded-md bg-[#C95EBE] hover:bg-[#b04da6] text-white text-[14px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;
